import { useState, type ReactNode } from 'react'
import { LogoMark } from './Logo'
import SmartStyleEditor from './SmartStyleEditor'
import { useGrammarly } from '@/hooks/useGrammarly'
import { STYLE_MAX_LENGTH } from '@/lib/constants'
import type { PromptAnalysis, SuggestionCard } from '@/lib/api'

interface Props {
  value: string
  onChange: (v: string) => void
  analysis: PromptAnalysis | null
  onApply: (card: SuggestionCard, suggestion: string) => void
  maxLength?: number
  label?: string
  /** The active intent preset + a setter — surfaced in the report panel. Optional: when
   *  omitted (e.g. the extension), the editor hides the picker and grades as full-song. */
  intent?: string
  onIntentChange?: (intent: string) => void
  /** Right-aligned action under the field (e.g. "Copy for Suno" / "Insert into Suno"). */
  action?: ReactNode
}

// The Style prompt editor. Defaults to the Promptioso smart card (inline marks + in-field
// score bar). Turning it off (via the logo menu) drops to a plain field — handing it to
// Grammarly when present — with our branded "Check prompt" button to turn it back on. Also
// falls back to plain when analysis isn't available yet (e.g. anonymous, pre-fetch).
export default function StyleField({
  value,
  onChange,
  analysis,
  onApply,
  maxLength = STYLE_MAX_LENGTH,
  label = 'Styles',
  intent,
  onIntentChange,
  action,
}: Props) {
  const [mode, setMode] = useState<'smart' | 'plain'>('smart')
  const grammarly = useGrammarly()
  const smart = mode === 'smart' && !!analysis

  return (
    <div>
      <label className="block text-sm font-semibold uppercase tracking-wide text-gray-500">{label}</label>

      <div className="mt-2">
        {smart ? (
          <SmartStyleEditor
            value={value}
            onChange={onChange}
            analysis={analysis!}
            onApply={onApply}
            maxLength={maxLength}
            intent={intent}
            onIntentChange={onIntentChange}
            onTurnOff={() => setMode('plain')}
          />
        ) : analysis ? (
          // Grammar mode: plain field for Grammarly, but the bottom bar stays ours — a branded
          // "Check prompt" button toggles back to the Promptioso evaluator.
          <div className="rounded-xl border border-gray-700 bg-gray-900 p-2 focus-within:ring-1 focus-within:ring-primary-700/50">
            <textarea
              value={value}
              maxLength={maxLength}
              onChange={(e) => onChange(e.target.value)}
              rows={3}
              data-enable-grammarly="true"
              placeholder="describe your track…"
              className="w-full resize-none border-0 bg-transparent px-1 py-1 font-mono text-sm text-gray-100 outline-none focus:ring-0"
            />
            <div className="mt-1.5 flex items-center gap-2 border-t border-gray-800 pt-1.5 text-xs">
              <button
                onClick={() => setMode('smart')}
                title="Open the Promptioso prompt evaluator"
                className="inline-flex items-center gap-1.5 rounded-full border border-primary-700 bg-gray-900 py-1 pl-1.5 pr-3 font-semibold text-primary-300 hover:bg-gray-800"
              >
                <LogoMark size="sm" className="h-4 w-4 shrink-0" />
                <span className="tabular-nums">{analysis.score}%</span>
                <span>Check prompt</span>
              </button>
              {grammarly && (
                <span className="ml-auto flex items-center gap-1 text-gray-500">
                  <span className="font-bold text-emerald-400">G</span> Grammarly active
                </span>
              )}
            </div>
          </div>
        ) : (
          <textarea
            value={value}
            maxLength={maxLength}
            onChange={(e) => onChange(e.target.value)}
            rows={3}
            className="w-full rounded-xl border border-gray-700 bg-gray-900 p-3 font-mono text-sm text-gray-100"
          />
        )}
      </div>

      <div className="mt-1 flex items-center justify-between text-xs text-gray-500">
        <span>{value.length}/{maxLength}</span>
        {action}
      </div>
    </div>
  )
}
